import { useCallback } from 'react';
import { useDeviceDetection } from './useDeviceDetection';
import { logger } from "@/lib/logger";

// Extend Window interface for iOS Haptics API
declare global {
  interface Window {
    Haptics?: {
      impact?: (options: { style: 'light' | 'medium' | 'heavy' }) => void;
      selection?: () => void;
      notification?: (options: { type: 'success' | 'warning' | 'error' }) => void;
    };
  }
}

export type HapticFeedbackType = 'light' | 'medium' | 'heavy' | 'selection' | 'impact' | 'notification';

export interface HapticOptions {
  pattern?: number | number[];
  duration?: number;
  intensity?: number;
}

export function useHaptics() {
  const { isMobile, hasTouch } = useDeviceDetection();

  const triggerHaptic = useCallback((
    type: HapticFeedbackType = 'light',
    options: HapticOptions = {}
  ) => {
    // Only trigger haptics on mobile devices with touch support
    if (!isMobile || !hasTouch) {
      return;
    }

    try {
      // Modern Haptic Feedback API (iOS Safari 13+)
      if ('vibrate' in navigator && typeof navigator.vibrate === 'function') {
        let pattern: number | number[];
        
        switch (type) {
          case 'light':
            pattern = options.pattern || 10;
            break;
          case 'medium':
            pattern = options.pattern || 20;
            break;
          case 'heavy':
            pattern = options.pattern || 40;
            break;
          case 'selection':
            pattern = options.pattern || [5];
            break;
          case 'impact':
            pattern = options.pattern || [10, 50, 10];
            break;
          case 'notification':
            pattern = options.pattern || [100, 50, 100];
            break;
          default:
            pattern = 10;
        }
        
        navigator.vibrate(pattern);
      }
      
      // iOS-specific Haptic Feedback (if available)
      if ('Haptics' in window && window.Haptics) {
        const haptics = window.Haptics;
        
        switch (type) {
          case 'light':
            haptics.impact?.({ style: 'light' });
            break;
          case 'medium':
            haptics.impact?.({ style: 'medium' });
            break;
          case 'heavy':
            haptics.impact?.({ style: 'heavy' });
            break;
          case 'selection':
            haptics.selection?.();
            break;
          case 'notification':
            haptics.notification?.({ type: 'success' });
            break;
          default:
            haptics.impact?.({ style: 'light' });
        }
      }
    } catch (error) {
      // Silently fail if haptics are not supported
      logger.debug('Haptic feedback not supported:', { error });
    }
  }, [isMobile, hasTouch]);

  const isHapticsSupported = useCallback(() => {
    return isMobile && hasTouch && (
      ('vibrate' in navigator) || 
      ('Haptics' in window)
    );
  }, [isMobile, hasTouch]);

  return {
    triggerHaptic,
    isHapticsSupported,
    // Convenience methods for common haptic patterns
    light: () => triggerHaptic('light'),
    medium: () => triggerHaptic('medium'),
    heavy: () => triggerHaptic('heavy'),
    selection: () => triggerHaptic('selection'),
    impact: () => triggerHaptic('impact'),
    notification: () => triggerHaptic('notification'),
    // Custom patterns
    success: () => triggerHaptic('notification', { pattern: [50, 100, 50] }),
    error: () => triggerHaptic('heavy', { pattern: [100, 50, 100, 50, 100] }),
    warning: () => triggerHaptic('medium', { pattern: [50, 50, 50] }),
    swipe: () => triggerHaptic('light', { pattern: [10] }),
    longPress: () => triggerHaptic('medium', { pattern: [20, 10, 20] }),
  };
}
